import type { ReactNode, RefObject } from 'react';
import { Modal as UiModal } from '@safetyhub/ui';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  children: ReactNode;
  /**
   * 하단 액션 영역 — base [[Modal]]의 `footer`로 그대로 넘어간다. 주면 푸터가 **고정**되고
   * 본문만 스크롤한다(`bodyScroll`).
   *
   * ⛔ **`<form>` 안의 submit 버튼을 여기로 옮기려면 `form` 속성이 필요하다.** base는 본문과
   * 푸터를 **형제 div**로 렌더하므로 푸터는 children의 `<form>` **밖**이다 — `type="submit"`만
   * 들고 오면 네이티브 제출이 조용히 끊긴다(tsc·spec 어느 것도 못 본다). children의 폼에
   * `id`를 주고 버튼에 `form={그 id}`를 달 것(`Button`이 `ButtonHTMLAttributes`를 확장해
   * `<button>`에 그대로 넘긴다). `EditModalFooter`·`StepFooter`는 `submitForm`·`nextForm`으로 받는다.
   *
   * ⚠️ 푸터를 여기로 넘기면서 **`useModalRequestClose`를 부모 본문에서 부르지 말 것** — 그 context는
   * Modal의 children/footer **하위 컴포넌트**에서만 잡힌다(부모 본문에서는 null). 푸터를 작은
   * 컴포넌트로 분리하는 것이 규약이고 `EditModalFooter`가 그 자리를 차지한다.
   */
  footer?: ReactNode;
  /** 배경 클릭 시 닫기 비활성화 (업로드 중 등) */
  preventClose?: boolean;
  /**
   * 미저장 변경 닫기 보호 — true면 Esc·오버레이·✕·footer 취소(`useModalRequestClose`)가
   * 확인 모달을 거친 뒤에만 onClose를 부른다. dirty 계산은 `useDirtySnapshot` 훅으로.
   * `preventClose`(제출 진행 중)가 켜져 있으면 **확인조차 없이** 닫기 요청이 전부 무시된다
   * (제출 중 닫기 차단이 dirty 확인보다 우선).
   */
  isDirty?: boolean;
  /**
   * 열릴 때 포커스를 줄 요소 — base로 그대로 넘긴다. 본문 요소의 `autoFocus`는 쓰지 말 것:
   * base가 마운트 effect에서 포커스를 dialog 컨테이너로 옮겨 커밋 직후 덮는다.
   */
  initialFocusRef?: RefObject<HTMLElement | null>;
}

/**
 * web 모달 어댑터 — 공용 `@safetyhub/ui`의 base [[Modal]]에 web의 기존 props(`isOpen`/`title`/
 * `description`/`preventClose`)를 매핑한다.
 *
 * 이 파일은 원래 자체 오버레이·포탈·스크롤 잠금·Esc를 직접 구현한 raw 모달이었다. 30여 개
 * 소비처가 이 API로 붙어 있어 **컴포넌트를 지우는 대신 어댑터로 바꿨다** — 소비처는 그대로 두고
 * 오버레이/포커스/키 UX와 패널 시각(라운드 20px·모달 그림자)이 한 번에 정본으로 수렴한다.
 *
 * base가 이미 보장하므로 여기서 다시 구현하지 않는 것: 포탈, `role="dialog"`+aria, Esc,
 * 오버레이 클릭 닫기, body 스크롤 잠금, 포커스 트랩, 닫힘 시 opener 포커스 복원, z-[70].
 *
 * `preventClose`(업로드 중 등)는 "닫을 수단을 전부 막는다"는 뜻이라 오버레이·Esc·✕ 셋 다 끈다.
 */
export default function Modal({
  isOpen,
  onClose,
  title,
  description,
  size = 'md',
  children,
  footer,
  preventClose = false,
  isDirty = false,
  initialFocusRef,
}: ModalProps) {
  return (
    <UiModal
      open={isOpen}
      initialFocusRef={initialFocusRef}
      onClose={onClose}
      title={title}
      footer={footer}
      size={size}
      closeOnOverlay={!preventClose}
      closeOnEsc={!preventClose}
      showCloseButton={!preventClose}
      isDirty={isDirty}
      // preventClose는 "닫을 수단 전부 차단"이라 dirty 확인보다 우선한다 — footer 취소가
      // 버튼 disabled를 빠뜨려도 base가 requestClose를 무시해 제출 중 닫힘이 없다.
      closeLocked={preventClose}
      // 긴 폼 모달이 뷰포트를 넘칠 때 **본문만** 스크롤한다(헤더·푸터 고정).
      //
      // 종전 값은 `panelClassName="max-h-[90vh] overflow-y-auto"`였고 그 이유는 「base는
      // 오버레이를 flex 중앙 정렬로 쓰므로 그 자리에선 위쪽 넘침에 손이 닿지 않는다」였다.
      // `bodyScroll`은 패널을 `max-h-full`로 제한해 **위쪽 넘침 자체를 없애므로** 그 이유가
      // 해소된다 — 우회가 아니라 같은 문제의 정본 처방이다. 덤으로 푸터(`footer` prop)가
      // 고정되어 긴 본문에서 액션 버튼이 밀리지 않는다(줄기 `shared-modal-ux-base` 3단계).
      //
      // ⚠️ 그 전환으로 클리핑 경계가 **패널 `overflow-y-auto` → 패널 `overflow-hidden` + 본문
      // `overflow-y-auto`**로 바뀐다. 두 축 모두 클리핑이므로 새로 잘리는 자리는 없지만,
      // 본문에서 패널 밖으로 삐져나오려는 절대배치 자식은 전과 같이 잘린다(예:
      // `CreateInspectionModal`의 푸터 말풍선 주석이 그 기제를 이름으로 지목한다).
      //
      // ⚠️ **2026-10-27 base 기본값이 `true`로 뒤집혀 이 줄은 «이제 중복»이다 — 아래 ⛔의 전제가
      // 바뀠다.** 지워도 기본값이 본문 스크롤을 주므로 「지우면 푸터가 밀린다」는 **더는 참이
      // 아니다**(그 flip의 탐지기는 `packages/ui`의 `Modal.test.tsx` `bodyScroll` describe에 있는
      // 「기본값 계약」 ↔ 「명시 opt-out」 **한 쌍**이다). 그래도 **남겨 둔다** — 이 어댑터는 web
      // 모달 30파일의 단일 소비처라 그 축을 **명시로** 들고 있는 편이 낫고, 아래 가드 케이스가
      // 그 명시를 이름으로 지키고 있다. 지우려면 그 케이스를 함께 처분할 것.
      //
      // ⛔ **이 주석에 opt-out 표기를 리터럴로 쓰지 말 것** — 그 표기는 이 `<UiModal>`의 **props
      // 블록 안**에 들어가므로, 주석을 걷지 않는 census는 이 태그를 「본문 스크롤을 끈 자리」로
      // 읽는다. 이 어댑터가 그 오판의 최악 자리다(30파일의 축을 혼자 소유한다). 2026-10-27에
      // 실제로 그렇게 적었다가 채택 집계가 6 → 5로 보여 **여유 0**이라 오판했다(가드 본체는
      // 주석을 strip해 옳았고 틀린 것은 그 census였다).
      //
      // ⛔ **이 한 줄을 지우지 말 것** — (flip 전 사유) 지우면 푸터가 다시 본문과 함께 밀리고
      // 패널 높이 제한이 사라진다. ①(footer ∧ 패널 스크롤)도 ②(채택 하한)도 **그 「지우기만
      // 하는」 되돌림을 못 잡아서**(2026-10-27 프로브 실측 0 red) 그 축의 탐지기를 따로 세웠다:
      // `__tests__/modalBodyScrollCallSiteGuard.test.ts`의 「web 어댑터가 bodyScroll을 든다」.
      bodyScroll
    >
      {description && <p className="mb-4 text-sm text-ink-500">{description}</p>}
      {children}
    </UiModal>
  );
}

/*
 * 종전 이 자리에 있던 푸터 래퍼(`border-t` + `space-x-3`를 children 안에 그리던 것)는 소비처
 * 29자리를 전부 `footer` prop으로 옮긴 뒤 지웠다(줄기 `shared-modal-ux-base` 3단계).
 * **푸터를 children 안에 그리는 래퍼를 다시 만들지 말 것** — 구분선·간격·고정 배치는 base가
 * 소유하고, children 안에 두면 본문 스크롤 영역에 갇혀 긴 본문에서 액션 버튼이 함께 밀린다.
 * 그 래칫은 `__tests__/modalBodyScrollCallSiteGuard.test.ts`가 맡는다.
 */
